import { ThemeSettingsSelection } from '@lessonup/pin-renderer';
import { Pin, PinComponentType } from '@lessonup/pins-shared';
import { Checkbox, spacing, SpaceBetween, styled, Switch, Divider, TextElement } from '@lessonup/ui-components';
import React from 'react';
import { useFeatureFlags } from '../../../../../utils/featureFlags/useFeatureFlags';
import { useEditorTranslation } from '../../../hooks/useEditorTranslation';
import { countPinsWithCustomBackgrounds, getAggregatedComponentTypeGroups, getPinsHaveCustomSettings } from './utils';

interface ThemeSettingsPanelProps {
  allPins: Pin[];
  selections: ThemeSettingsSelection;
  onSelectionChange: (selections: ThemeSettingsSelection) => void;
  applyingToAllSlides: boolean;
  onApplyToAllSlidesChange: (value: boolean) => void;
  hasCustomSettings: boolean;
}

export const ThemeSettingsPanel: React.FC<ThemeSettingsPanelProps> = ({
  allPins,
  selections,
  onSelectionChange,
  applyingToAllSlides,
  onApplyToAllSlidesChange,
  hasCustomSettings,
}) => {
  const { t } = useEditorTranslation();
  const { isFeatureEnabled } = useFeatureFlags();
  const componentGroups = hasCustomSettings ? getAggregatedComponentTypeGroups(allPins, t, applyingToAllSlides) : [];
  const hasCustomPinSettings = hasCustomSettings ? getPinsHaveCustomSettings(allPins) : false;
  const backgroundCount = applyingToAllSlides ? countPinsWithCustomBackgrounds(allPins) : undefined;

  const handlePinSettingToggle = (shouldKeep: boolean) => {
    onSelectionChange({
      ...selections,
      pinSettings: {
        ...selections.pinSettings,
        background: !shouldKeep,
      },
    });
  };

  const handleComponentSettingToggle = (type: PinComponentType, shouldKeep: boolean) => {
    onSelectionChange({
      ...selections,
      componentSettings: {
        ...selections.componentSettings,
        [type]: !shouldKeep,
      },
    });
  };

  const hasAnyCustomSettings = hasCustomSettings && (hasCustomPinSettings || componentGroups.length > 0);
  const overriddenSettingsCount = (hasCustomPinSettings ? 1 : 0) + componentGroups.length;
  const itemsText = overriddenSettingsCount === 1 ? 'element' : 'elements';

  return (
    <StyledContainer>
      <StyledSection>
        <StyledToggleRow>
          <Switch
            id="apply-to-all"
            ariaLabel={t('editor.themeModal.settings.applyToAllSlides')}
            label={t('editor.themeModal.settings.applyToAllSlides')}
            checked={applyingToAllSlides}
            onChange={(e) => onApplyToAllSlidesChange(e.target.checked)}
          />
        </StyledToggleRow>
        {hasAnyCustomSettings && isFeatureEnabled('editorPinThemeOverrideSettings') && (
          <>
            <Divider orientation="horizontal" />
            <StyledCustomItemsSection>
              <TextElement textStyle="label" weight="bold">
                {t('editor.themeModal.settings.customizedItems', { defaultValue: 'Customized settings' })}
              </TextElement>
              <TextElement textStyle="bodyText" weight="regular">
                {t('editor.themeModal.settings.customizedItemsDescription', {
                  count: overriddenSettingsCount,
                  defaultValue: `${overriddenSettingsCount} customized ${itemsText} may be overwritten by the theme settings.`,
                })}
              </TextElement>
            </StyledCustomItemsSection>

            <StyledCheckboxContainer>
              <SpaceBetween direction="y" spacing={spacing.size4}>
                {hasCustomPinSettings && (
                  <StyledCheckboxItem>
                    <Checkbox
                      id="pin-background"
                      checked={!(selections.pinSettings.background ?? true)}
                      onChange={(e) => handlePinSettingToggle(e.target.checked)}
                      label={
                        backgroundCount
                          ? t('editor.themeModal.settings.backgroundWithCount', { count: backgroundCount })
                          : t('editor.themeModal.settings.background')
                      }
                    />
                  </StyledCheckboxItem>
                )}
                {componentGroups.map((group) => {
                  const affectedCount = 'affectedPinCount' in group ? group.affectedPinCount : undefined;
                  const label = affectedCount
                    ? t('editor.themeModal.settings.componentWithCount', { type: group.label, count: affectedCount })
                    : group.label;
                  const groupType: PinComponentType = group.type;
                  return (
                    <StyledCheckboxItem key={groupType}>
                      <Checkbox
                        id={`component-${groupType}`}
                        checked={!(selections.componentSettings[groupType] ?? true)}
                        onChange={(e) => handleComponentSettingToggle(groupType, e.target.checked)}
                        label={label}
                      />
                    </StyledCheckboxItem>
                  );
                })}
              </SpaceBetween>
            </StyledCheckboxContainer>
          </>
        )}
      </StyledSection>
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.size0};
`;

const StyledSection = styled.div`
  padding: ${spacing.size12};
  display: flex;
  flex-direction: column;
  gap: ${spacing.size8};
`;

const StyledToggleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${spacing.size8};
`;

const StyledCustomItemsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.size6};
`;

const StyledCheckboxContainer = styled.div`
  padding-left: ${spacing.size8};
  padding-top: ${spacing.size8};
`;

const StyledCheckboxItem = styled.div`
  padding: ${spacing.size4} 0;
`;
